/**
 * Text tokenizer for search indexing
 * Handles Unicode normalization and word boundary splitting
 */

// Common English stop words (configurable)
const STOP_WORDS = new Set([
  'a', 'an', 'and', 'are', 'as', 'at', 'be', 'by', 'for', 'from',
  'has', 'he', 'in', 'is', 'it', 'its', 'of', 'on', 'that', 'the',
  'to', 'was', 'were', 'will', 'with', 'the', 'this', 'but', 'they',
  'have', 'had', 'what', 'when', 'where', 'who', 'which', 'why', 'how'
]);

export interface TokenizerOptions {
  lowercase?: boolean;       // Default: true
  removeStopWords?: boolean; // Default: false (preserve for better recall)
  minLength?: number;        // Default: 2
}

const DEFAULT_OPTIONS: Required<TokenizerOptions> = {
  lowercase: true,
  removeStopWords: false,
  minLength: 2,
};

/**
 * Tokenize text into searchable tokens
 * 
 * @param text - Raw text to tokenize
 * @param options - Tokenizer options
 * @returns Array of normalized tokens
 */
export function tokenize(text: string | null | undefined, options?: TokenizerOptions): string[] {
  if (!text) return [];

  const opts = { ...DEFAULT_OPTIONS, ...options };

  // 1. Unicode normalize (NFKC - compatibility decomposition + canonical composition)
  let normalized = text.normalize('NFKC');

  // 2. Lowercase if enabled
  if (opts.lowercase) {
    normalized = normalized.toLowerCase();
  }

  // 3. Extract words using Unicode-aware regex
  // Matches sequences of Unicode letters and numbers
  const words = normalized.match(/[\p{L}\p{N}]+/gu) || [];

  // 4. Filter by minimum length and optionally remove stop words
  const tokens = words.filter(word => {
    if (word.length < opts.minLength) return false;
    if (opts.removeStopWords && STOP_WORDS.has(word)) return false;
    return true;
  });

  return tokens;
}

/**
 * Tokenize text and return with positions (for phrase matching)
 * 
 * @param text - Raw text to tokenize
 * @param options - Tokenizer options
 * @returns Array of tokens with their positions
 */
export function tokenizeWithPositions(
  text: string | null | undefined,
  options?: TokenizerOptions
): Array<{ token: string; position: number }> {
  if (!text) return [];

  const opts = { ...DEFAULT_OPTIONS, ...options };
  let normalized = text.normalize('NFKC');
  
  if (opts.lowercase) {
    normalized = normalized.toLowerCase();
  }

  const result: Array<{ token: string; position: number }> = [];
  const regex = /[\p{L}\p{N}]+/gu;
  let match: RegExpExecArray | null;
  let position = 0;

  while ((match = regex.exec(normalized)) !== null) {
    const word = match[0];
    if (word.length >= opts.minLength) {
      if (!opts.removeStopWords || !STOP_WORDS.has(word)) {
        result.push({ token: word, position });
        position++;
      }
    }
  }

  return result;
}

/**
 * Check if a token is a stop word
 */
export function isStopWord(token: string): boolean {
  return STOP_WORDS.has(token.toLowerCase());
}

